<template>
  <v2-container class="pb-20">
    <v2-row>
      <v2-col col12>
        <v2-header-bar mobileFlyOut :titles="[{ label: $t('quickLaunch.title') }]">
          <template v-slot:left>
            <v2-steps :steps="steps"></v2-steps>
          </template>
          <template v-slot:right>
            <v2-button
              :title="$t('quickLaunch.actions.goToPreview')"
              round
              icon
              primary
              outline
              class="mr-2"
              :active="currentStep > 0"
              @click.native="openBuilder(true)"
            >
              <v2-icon :data="require('@icon/Icon=eye.svg')" color="inherit" width="auto" height="22px"></v2-icon>
            </v2-button>
            <v2-button
              :title="$t('quickLaunch.actions.goToBuilder')"
              round
              icon
              primary
              outline
              class="mr-2"
              :active="currentStep > 0"
              @click.native="openBuilder(false)"
            >
              <v2-icon :data="require('@icon/Icon=edit-2.svg')" color="inherit" width="18px"></v2-icon>
            </v2-button>
            <v2-button
              :title="$t('quickLaunch.actions.goToShareProject')"
              round
              icon
              primary
              outline
              class="mr-2"
              :active="currentStep === 1"
              v-if="currentStep === 0 || currentStep === 1"
              @click.native="goToShareProject()"
            >
              <v2-icon :data="require('@icon/Icon=send.svg')" color="inherit" width="auto" height="22px"></v2-icon>
            </v2-button>
            <v2-button
              :title="$t('quickLaunch.actions.goToCompleteQuickLaunch')"
              round
              icon
              primary
              :active="currentStep === 1"
              v-if="currentStep === 0 || currentStep === 1"
              @click.native="completeQuickLaunch()"
            >
              <v2-icon :data="require('@icon/Icon=check.svg')" color="inherit" width="auto" height="22px"></v2-icon>
            </v2-button>
          </template>
        </v2-header-bar>
      </v2-col>
    </v2-row>
    <v2-row class="relative h-full" v-if="isCustomLoading">
      <v-spinner :loading="isCustomLoading"></v-spinner>
    </v2-row>
    <v2-row noSpace v-if="currentStep < 2 && !isCustomLoading">
      <v2-row>
        <v2-col col12>
          <h2 class="flex items-center" :class="{ disabled: currentStep > 0 }">
            <div class="step-number" :class="{ success: currentStep > 0 }">
              <span v-if="currentStep === 0">1</span>
              <v2-icon
                v-if="currentStep > 0"
                :data="require('@icon/Icon=check.svg')"
                color="#fff"
                width="16px"
                height="16px"
              ></v2-icon>
            </div>
            <div v-if="currentStep === 0">
              {{ $t("quickLaunch.titles.createNewProject") }}
            </div>
            <div v-if="currentStep > 0">
              {{ $t("quickLaunch.titles.projectCreated") }}
            </div>
          </h2>
        </v2-col>
      </v2-row>
      <v2-row>
        <v2-col col12 noSpaceX v-if="currentStep === 0">
          <div>
            <form @submit.prevent data-vv-scope="createProject">
              <div v-if="createNewClient">
                <v2-row>
                  <v2-col col12 noSpace>
                    <v2-section-heading
                      :title="$t('quickLaunch.titles.createClient')"
                      :link="$t('quickLaunch.actions.chooseClient')"
                      :linkAction="
                        () => {
                          toggleCreateClientForm();
                        }
                      "
                    ></v2-section-heading>
                  </v2-col>
                  <v2-col col6 noSpace>
                    <v2-textfield
                      :label="$t('quickLaunch.labels.firstName')"
                      :placeholder="$t('forms.required')"
                      v-model="firstName"
                      v-validate="'required'"
                      name="firstName"
                      :error="errors.first('createProject.firstName')"
                      :data-vv-as="$t('quickLaunch.labels.firstName')"
                    ></v2-textfield>
                  </v2-col>
                  <v2-col col6 noSpace>
                    <v2-textfield
                      :label="$t('quickLaunch.labels.lastName')"
                      :placeholder="$t('forms.required')"
                      v-model="lastName"
                      v-validate="'required'"
                      name="lastName"
                      :error="errors.first('createProject.lastName')"
                      :data-vv-as="$t('quickLaunch.labels.lastName')"
                    ></v2-textfield>
                  </v2-col>
                  <v2-col col6 noSpace>
                    <v2-textfield
                      :label="$t('quickLaunch.labels.email')"
                      :placeholder="$t('forms.required')"
                      v-model="email"
                      v-validate="'required|email'"
                      name="email"
                      :error="errors.first('createProject.email')"
                      :data-vv-as="$t('quickLaunch.labels.email')"
                    ></v2-textfield>
                  </v2-col>
                  <v2-col col6 noSpace>
                    <v2-select
                      :label="$t('quickLaunch.labels.gender')"
                      :placeholder="$t('forms.required')"
                      v-model="gender"
                      v-validate="'required'"
                      :data-vv-as="$t('quickLaunch.labels.gender')"
                      name="gender"
                      optionLabel="label"
                      :error="errors.first('createProject.gender')"
                      :options="genderOptions"
                    ></v2-select>
                  </v2-col>
                  <v2-col col6 noSpace>
                    <v2-select
                      :label="$t('quickLaunch.labels.locale')"
                      :placeholder="$t('forms.required')"
                      v-model="locale"
                      v-validate="'required'"
                      :data-vv-as="$t('quickLaunch.labels.locale')"
                      name="locale"
                      optionLabel="label"
                      :error="errors.first('createProject.locale')"
                      :options="localeOptions"
                    ></v2-select>
                  </v2-col>
                </v2-row>
              </div>
              <v2-row v-if="!createNewClient">
                <v2-col col12 noSpace>
                  <v2-section-heading
                    :link="$t('quickLaunch.actions.createClient')"
                    :linkAction="
                      () => {
                        toggleCreateClientForm();
                      }
                    "
                  >
                    <template v-slot:title>
                      <div class="flex items-center">
                        {{ $t("quickLaunch.titles.setClient") }}
                        <v2-tooltip
                          class="ml-2"
                          :title="$t('tips.title')"
                          :content="$t('tips.quickLaunch.chooseClient')"
                          v-if="showAlways || showTips"
                          :show="showTips"
                        ></v2-tooltip>
                      </div>
                    </template>
                  </v2-section-heading>
                </v2-col>
                <v2-col col6 noSpace class="relative">
                  <v2-select
                    :label="$t('quickLaunch.labels.client')"
                    :placeholder="$t('forms.required')"
                    v-model="client"
                    v-validate="'required'"
                    :data-vv-as="$t('quickLaunch.labels.client')"
                    name="client"
                    optionLabel="last_name"
                    :error="errors.first('createProject.client')"
                    :options="clientOptions"
                    :searchable="true"
                    :loading="isLoading"
                    :internalSearch="false"
                    @searchChange="loadClients"
                    :customLabel="setCustomLabel"
                  ></v2-select>
                </v2-col>
              </v2-row>
              <v2-row>
                <v2-col col12 noSpace>
                  <v2-section-heading>
                    <template v-slot:title>
                      <div class="flex items-center">
                        {{ $t("quickLaunch.titles.createProject") }}
                        <v2-tooltip
                          class="ml-2"
                          :title="$t('tips.title')"
                          :content="$t('tips.quickLaunch.createProject')"
                          v-if="showAlways || showTips"
                          :show="showTips"
                        ></v2-tooltip>
                      </div>
                    </template>
                  </v2-section-heading>
                </v2-col>
                <v2-col col6 noSpace>
                  <v2-textfield
                    :label="$t('quickLaunch.labels.projectName')"
                    :placeholder="$t('forms.required')"
                    v-model="projectName"
                    v-validate="'required'"
                    name="title"
                    :error="errors.first('createProject.title')"
                    :data-vv-as="$t('quickLaunch.labels.projectName')"
                  ></v2-textfield>
                </v2-col>
                <v2-col col6 noSpace>
                  <v2-select
                    :label="$t('quickLaunch.labels.template')"
                    :placeholder="$t('forms.required')"
                    :value="projectTemplate"
                    v-model="projectTemplate"
                    v-validate="'required'"
                    :data-vv-as="$t('quickLaunch.labels.template')"
                    name="projectTemplate"
                    optionLabel="name"
                    :error="errors.first('createProject.projectTemplate')"
                    :options="templateOptions"
                    :searchable="true"
                    :internalSearch="true"
                  ></v2-select>
                </v2-col>
                <v2-col col6 class="justify-center">
                  <v2-checkbox :label="$t('quickLaunch.labels.privateProject')" v-model="privateProject"></v2-checkbox>
                </v2-col>
                <v2-col col6 class="items-end">
                  <v2-button primary fixedWidth @click.native="submitProject()"
                    >{{ $t("quickLaunch.actions.createProject") }}
                  </v2-button>
                </v2-col>
              </v2-row>
            </form>
          </div>
        </v2-col>
        <v2-col col12 noSpaceX v-if="currentStep > 0 && currentProject && !isCustomLoading">
          <div>
            <v2-row>
              <v2-col col12 noSpace>
                <v2-section-heading
                  :disabled="currentStep > 0"
                  :title="$t('quickLaunch.titles.clientData')"
                ></v2-section-heading>
              </v2-col>
              <v2-col col6 noSpace>
                <v2-textfield
                  disabled
                  :label="$t('quickLaunch.labels.name')"
                  :value="$h.formatName(currentProject.owner, true)"
                ></v2-textfield>
              </v2-col>
              <v2-col col6 noSpace>
                <v2-textfield
                  disabled
                  :label="$t('quickLaunch.labels.email')"
                  :value="currentProject.owner.email"
                ></v2-textfield>
              </v2-col>
            </v2-row>
            <v2-row>
              <v2-col col12 noSpace>
                <v2-section-heading
                  :disabled="currentStep > 0"
                  :title="$t('quickLaunch.titles.projectData')"
                ></v2-section-heading>
              </v2-col>
              <v2-row noSpace>
                <v2-col col6 noSpace>
                  <v2-textfield
                    disabled
                    :label="$t('quickLaunch.labels.projectName')"
                    :value="currentProject.title"
                  ></v2-textfield>
                </v2-col>
                <v2-col col6 noSpace>
                  <v2-textfield
                    disabled
                    :label="$t('quickLaunch.labels.projectTemplate')"
                    :value="currentProject.template.name"
                  ></v2-textfield>
                </v2-col>
              </v2-row>
            </v2-row>
          </div>
        </v2-col>
      </v2-row>
    </v2-row>
    <v2-row v-if="currentStep < 2 && !isCustomLoading">
      <v2-row>
        <v2-col col12>
          <h2 class="flex items-center" :class="{ disabled: currentStep === 0 }">
            <div class="step-number" :class="{ disabled: currentStep === 0 }">
              <span v-if="currentStep > 0">2</span>
              <v2-icon
                v-if="currentStep === 0"
                :data="require('@icon/Icon=lock.svg')"
                color="#fff"
                width="14px"
              ></v2-icon>
            </div>
            <div class="flex items-center">
              {{ $t("quickLaunch.titles.fillProject") }}
              <v2-tooltip
                class="ml-2"
                :title="$t('tips.title')"
                :content="$t('tips.quickLaunch.fillProject')"
                v-if="showAlways || showTips"
                :show="showTips"
              ></v2-tooltip>
            </div>
          </h2>
        </v2-col>
      </v2-row>
      <v2-row v-if="currentStep > 0 && currentProject && !isCustomLoading">
        <v2-col col12 noSpaceX v-if="pagesOptions">
          <v2-row noSpace>
            <hr class="w-full mb-4" />
            <v2-col>
              <h2 class="flex items-center">
                <div class="step-number">
                  <span>2.1</span>
                </div>
                <div class="flex items-center">
                  {{ $t("quickLaunch.titles.editPages") }}
                </div>
              </h2>
            </v2-col>
          </v2-row>
          <v2-row noSpace>
            <v2-col>
              <v2-select
                withoutFormError
                :placeholder="$t('placeholders.searchForPage')"
                v-model="selectedPages"
                :options="pagesOptions"
                optionLabel="name"
                :multiple="true"
                trackBy="name"
                :maxHeight="350"
                id="pages"
                name="pages"
                :searchable="true"
                :internalSearch="true"
                :hideSelected="true"
                openDir="bottom"
                @select="
                  (selectedOption) => {
                    setPageStatus(selectedOption);
                  }
                "
                @remove="
                  (removedOption) => {
                    setPageStatus(removedOption);
                  }
                "
              >
                <template slot="customTag" slot-scope="{ option, remove }">
                  <span class="multiselect__tag" v-if="option.overridable">
                    <span>{{ option.name }}</span>
                    <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon" @click="remove(option)"></i>
                  </span>
                  <span class="multiselect__tag" style="opacity: 0.7" v-if="!option.overridable">
                    <span>{{ option.name }}</span>
                  </span>
                </template>
              </v2-select>
            </v2-col>
          </v2-row>
        </v2-col>
      </v2-row>
      <v2-row v-if="currentStep > 0 && currentProject && !isCustomLoading">
        <v2-col col12 noSpaceX v-if="currentProjectComponents && textTemplateSnippetOptions && contactOptions">
          <v2-row noSpace>
            <hr class="w-full mb-4" />
            <v2-col>
              <h2 class="flex items-center">
                <div class="step-number">
                  <span>2.2</span>
                </div>
                <div class="flex items-center">
                  {{ $t("quickLaunch.titles.fillContent") }}
                </div>
              </h2>
            </v2-col>
          </v2-row>
          <v2-row>
            <v2-col col12 v-if="currentProjectComponents">
              <v2-accordion :boxes="currentAccordionBoxes" fullWidth>
                <template v-slot:[component.uid] v-for="component in currentProjectComponents">
                  <ql-text-template
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-text-template'"
                    :textTemplateSnippets="textTemplateSnippetOptions"
                    :defaultSnippet="textDefaultTemplateSnippet"
                  ></ql-text-template>
                  <ql-file-list
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-file-list'"
                  ></ql-file-list>
                  <ql-contact
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-contact'"
                    :options="contactOptions"
                  ></ql-contact>
                  <ql-calendly
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-calendly-widget'"
                  ></ql-calendly>
                  <ql-image
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-image'"
                  ></ql-image>
                  <ql-html :component="component" :key="component.uid" v-if="component.tagname === 'tb-html'"></ql-html>
                  <ql-title
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-title'"
                  ></ql-title>
                  <ql-countdown
                    :component="component"
                    :key="component.uid"
                    v-if="component.tagname === 'tb-project-countdown'"
                  ></ql-countdown>
                </template>
              </v2-accordion>
            </v2-col>
          </v2-row>
        </v2-col>
      </v2-row>
      <v2-row>
        <v2-col col12>
          <h2 class="flex items-center" :class="{ disabled: currentStep === 0 }">
            <div class="step-number" :class="{ disabled: currentStep === 0 }">
              <span v-if="currentStep > 0">3</span>
              <v2-icon
                v-if="currentStep === 0"
                :data="require('@icon/Icon=lock.svg')"
                color="#fff"
                width="14px"
              ></v2-icon>
            </div>
            <div>
              {{ $t("quickLaunch.titles.shareProject") }}
            </div>
          </h2>
        </v2-col>
      </v2-row>
      <v2-row v-if="currentStep > 0 && currentProject && !isCustomLoading">
        <v2-col col12 noSpaceX noSpace id="send">
          <form @submit.prevent data-vv-scope="shareProject">
            <v2-project-share
              v-if="emailTemplateSnippetsReady"
              withFinishQuickLaunch
              :project="currentProject"
              :email-snippets="emailTemplateSnippetOptions"
              :default-snippet="emailDefaultTemplateSnippet"
              :hasPortalEnabled="teams[$route.params.team].account.team.attributes.portal_enabled"
              @sentMail="
                (data) => {
                  setSentStatus(data);
                }
              "
              @finishQuickLaunch="completeQuickLaunch"
            ></v2-project-share>
          </form>
        </v2-col>
        <v2-col col12>
          <v2-project-link
            :link="currentProject.links.frontend"
            :label="$t('quickLaunch.labels.projectLink')"
            :clickCallback="
              () => {
                copyLinkCallback();
              }
            "
          ></v2-project-link>
        </v2-col>
        <v2-col col12 v-if="teams[$route.params.team].account.team.attributes.portal_enabled">
          <v2-project-link
            :link="currentProject.owner.links.portal"
            :label="$t('quickLaunch.labels.portalLink')"
            :clickCallback="
              () => {
                copyLinkCallback(false);
              }
            "
          ></v2-project-link>
        </v2-col>
      </v2-row>
    </v2-row>
    <v2-row v-if="currentStep === 2 && !isCustomLoading" class="mt-30px" style="overflow-x: clip">
      <v2-confetti></v2-confetti>
      <v2-col col12 noSpace>
        <h1 class="mb-2.5 text-center">
          {{ $t("quickLaunch.titles.congrats") }}
        </h1>
      </v2-col>
      <!--Christmas edition-->
      <!--<v2-col col12 noSpace>-->
      <!--<h1 class="mb-2.5 text-center flex justify-center items-center">-->
      <!--<div class="relative">-->
      <!--<div class="relative z-20">{{ $t("quickLaunch.titles.congrats") }}</div>-->
      <!--<div class="relative z-10">-->
      <!--<div class="absolute" style="right: -125px; top: -80px">-->
      <!--<video width="160" height="120" autoplay muted loop>-->
      <!--<source src="@/assets/img/nikolaus.mp4" type=video/mp4>-->
      <!--</video>-->
      <!--</div>-->
      <!--</div>-->
      <!--</div>-->
      <!--</h1>-->
      <!--</v2-col>-->
      <v2-col col12 class="text-center">{{ $t("quickLaunch.titles.quickLaunchComplete") }}</v2-col>
    </v2-row>
    <v2-row v-if="currentStep === 2 && !isCustomLoading">
      <v2-col col4>
        <v2-big-icon-text-button
          :icon="require('@icon/Icon=rocket.svg')"
          iconClass="icon-rocket"
          iconHeight="18px"
          iconWidth="auto"
          color="#fff"
          :onClick="
            () => {
              $goTo('QuickLaunchWithClientAndProject', {
                client: currentProject.owner.uid,
                project: currentProject.uid,
              });
            }
          "
          :title="$t('quickLaunch.bigButtons.quickLaunchStart.title')"
          :text="$t('quickLaunch.bigButtons.quickLaunchStart.text')"
          >{{ $t("quickLaunch.bigButtons.quickLaunchStart.button") }}
        </v2-big-icon-text-button>
      </v2-col>
      <v2-col col4>
        <v2-big-icon-text-button
          :icon="require('@icon/Icon=share.svg')"
          iconHeight="18px"
          iconWidth="auto"
          color="#fff"
          :onClick="
            () => {
              $goTo('Project', { uid: currentProject.uid, tab: 'share' });
            }
          "
          :title="$t('quickLaunch.bigButtons.projectShare.title')"
          :text="$t('quickLaunch.bigButtons.projectShare.text')"
          >{{ $t("quickLaunch.bigButtons.projectShare.button") }}
        </v2-big-icon-text-button>
      </v2-col>
      <v2-col col4>
        <v2-big-icon-text-button
          :icon="require('@icon/Icon=inbox.svg')"
          color="#fff"
          iconHeight="22px"
          iconWidth="auto"
          :onClick="
            () => {
              setRedirectedInboxProject(currentProject);
              $goTo('Inbox');
            }
          "
          :title="$t('quickLaunch.bigButtons.projectInbox.title')"
          :text="$t('quickLaunch.bigButtons.projectInbox.text')"
          >{{ $t("quickLaunch.bigButtons.projectInbox.button") }}
        </v2-big-icon-text-button>
      </v2-col>
      <v2-col col4>
        <v2-big-icon-text-button
          :icon="require('@icon/Icon=folder.svg')"
          color="#fff"
          :iconFill="true"
          iconHeight="18px"
          iconWidth="auto"
          :onClick="
            () => {
              $goTo('Project', { uid: currentProject.uid });
            }
          "
          :title="$t('quickLaunch.bigButtons.projectDetails.title')"
          :text="$t('quickLaunch.bigButtons.projectDetails.text')"
          >{{ $t("quickLaunch.bigButtons.projectDetails.button") }}
        </v2-big-icon-text-button>
      </v2-col>
      <v2-col col4>
        <v2-big-icon-text-button
          :icon="require('@icon/Icon=folder.svg')"
          color="#fff"
          :iconFill="true"
          iconHeight="18px"
          iconWidth="auto"
          :onClick="
            () => {
              $goTo('Project', { uid: currentProject.uid, tab: 'extended-data' });
            }
          "
          :title="$t('quickLaunch.bigButtons.projectExtendedData.title')"
          :text="$t('quickLaunch.bigButtons.projectExtendedData.text')"
          >{{ $t("quickLaunch.bigButtons.projectExtendedData.button") }}
        </v2-big-icon-text-button>
      </v2-col>
      <v2-col col4>
        <v2-big-icon-text-button
          :icon="require('@icon/Icon=arrow-left.svg')"
          color="#fff"
          iconHeight="22px"
          iconWidth="auto"
          :onClick="
            () => {
              $router.push(startingRoute);
            }
          "
          :title="$t('quickLaunch.bigButtons.backToStart.title')"
          :text="$t('quickLaunch.bigButtons.backToStart.text')"
          >{{ $t("quickLaunch.bigButtons.backToStart.button") }}
        </v2-big-icon-text-button>
      </v2-col>
    </v2-row>
  </v2-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import QlTextTemplate from "./partials/quicklaunch/QuickLaunchTextTemplate";
import QlFileList from "./partials/quicklaunch/QuickLaunchFileList";
import QlContact from "./partials/quicklaunch/QuickLaunchContact";
import QlCalendly from "./partials/quicklaunch/QuickLaunchCalendly";
import QlImage from "./partials/quicklaunch/QuickLaunchImage";
import QlHtml from "./partials/quicklaunch/QuickLaunchHtml";
import QlTitle from "./partials/quicklaunch/QuickLaunchTitle";
import QlCountdown from "./partials/quicklaunch/QuickLaunchCountdown";

export default {
  name: "QuickLaunch",
  components: {
    QlTextTemplate,
    QlFileList,
    QlContact,
    QlCalendly,
    QlImage,
    QlHtml,
    QlTitle,
    QlCountdown,
  },
  data() {
    return {
      steps: [
        { number: 1, active: true, success: false },
        { number: 2, active: false, success: false },
        { number: 3, active: false, success: false },
        {
          number: 4,
          active: false,
          success: false,
          icon: require("@icon/Icon=rocket.svg"),
          iconClass: "icon-rocket",
          iconColor: "#5057AF",
        },
      ],
      isCustomLoading: false,
      currentStep: 0,
      createNewClient: false,
      isLoading: false,
      firstName: "",
      lastName: "",
      email: "",
      gender: "",
      genderOptions: [
        { label: this.$t("gender.female"), value: "f" },
        { label: this.$t("gender.male"), value: "m" },
      ],
      locale: { label: this.$t("lang.de"), value: "de" },
      localeOptions: [],
      client: "",
      clientOptions: [],
      clientName: "",
      projectName: "",
      contactOptions: [],
      templateOptions: [],
      projectTemplate: null,
      textDefaultTemplateSnippet: {},
      textTemplateSnippetOptions: [],
      emailDefaultTemplateSnippet: {},
      emailTemplateSnippetOptions: [],
      emailTemplateSnippetsReady: false,
      optionalValidation: {},
      privateProject: false,
      switchPages: [],
      pagesOptions: [],
      selectedPages: [],
      unsavedComponents: [],
      unsavedComponentsWithError: [],
    };
  },
  created() {
    this.init();
    this.setOptionalValidation();
    this.$eventbus.$on("setUnsavedComponents", this.setUnsavedComponents);
    this.$eventbus.$on("componentSavedSuccess", this.componentSavedSuccess);
    this.$eventbus.$on("componentSavedError", this.componentSavedError);
  },
  methods: {
    ...mapActions([
      "getTeamLanguages",
      "getClientList",
      "getClient",
      "getTeamTemplates",
      "createClient",
      "createProject",
      "getTeamContacts",
      "getTemplateSnippets",
      "getProject",
      "getProjectComponents",
      "createProjectFiles",
      "createComponentRelation",
      "setProjectPosted",
      "getProjectBuilderSession",
      "setLastSelectedDesignTemplate",
      "getProjectPages",
      "updateProjectPage",
      "setRedirectedInboxProject",
    ]),
    init() {
      if (!this.$route.params.project) {
        this.initFormData();
      } else if (this.$route.params.client && this.$route.params.project && !this.$route.query.completed) {
        this.isCustomLoading = true;
        this.currentStep = 1;
        this.steps = [
          { number: 1, active: false, success: true },
          { number: 2, active: true, success: false },
          { number: 3, active: true, success: false },
          {
            number: 4,
            active: false,
            success: false,
            icon: require("@icon/Icon=rocket.svg"),
            iconClass: "icon-rocket",
            iconColor: "#5057AF",
          },
        ];
        const vm = this;
        vm.getProject({
          customLoading: true,
          project: this.$route.params.project,
        })
          .then(() => {
            this.initProjectData();
          })
          .catch(() => {
            this.isCustomLoading = false;
          });
      } else if (this.$route.query.completed) {
        this.isCustomLoading = true;
        this.currentStep = 2;
        this.steps = [
          { number: 1, active: false, success: true },
          { number: 2, active: false, success: true },
          { number: 3, active: false, success: true },
          {
            number: 4,
            active: true,
            success: false,
            icon: require("@icon/Icon=rocket.svg"),
            iconClass: "icon-rocket",
            iconColor: "#ffffff",
          },
        ];
        const vm = this;
        vm.getProject({
          customLoading: true,
          project: this.$route.params.project,
        })
          .then(() => {
            this.isCustomLoading = false;
          })
          .catch(() => {
            this.isCustomLoading = false;
          });
      }
    },
    initFormData() {
      this.isCustomLoading = true;
      const p1 = this.setClientOptions();
      const p2 = this.getTemplates();
      const p3 = this.getLanguages();
      Promise.all([p1, p2, p3])
        .then(() => {
          this.isCustomLoading = false;
        })
        .catch(() => {
          this.isCustomLoading = false;
        });
    },
    getLanguages() {
      const vm = this;
      vm.getTeamLanguages({ customLoading: true }).then(() => {
        if (vm.teamLanguages) {
          vm.teamLanguages.forEach((lang) => {
            vm.localeOptions.push({
              label: vm.$t('lang["' + lang + '"]'),
              value: lang,
            });
          });
        }
      });
    },
    setPageStatus(selectedOption) {
      if (selectedOption.overridable) {
        const vm = this;
        vm.updateProjectPage({
          customLoading: true,
          params: { project: this.currentProject.uid, page: selectedOption.uid },
          data: { visible: !selectedOption.visible },
        })
          .then(() => {
            vm.$snotify.success(vm.$t("quickLaunch.toasts.editPage.success"), vm.$t("toasts.success"), {
              icon: vm.$successIcon,
            });
          })
          .catch(() => {
            vm.$snotify.error(vm.$t("quickLaunch.toasts.editPage.error"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          });
      }
    },
    setOptionalValidation() {
      if (this.teams[this.$route.params.team].account) {
        const teamAttributes = this.teams[this.$route.params.team].account.team.attributes;
        if (teamAttributes.optional_validation) {
          this.optionalValidation = teamAttributes.optional_validation.clients
            ? teamAttributes.optional_validation.clients
            : {};
        }
      }
    },
    resetCreateClientForm() {
      this.lastName = "";
      this.firstName = "";
      this.email = "";
      this.gender = "";
      this.locale = { label: this.$t("lang.de"), value: "de" };
    },
    toggleCreateClientForm() {
      this.createNewClient = !this.createNewClient;
    },
    setClientOptions() {
      const vm = this;
      const params = {
        per_page: "1000",
        order_col: "last_name",
        order_dir: "ASC",
        search: "",
      };
      this.getClientList({ params, customLoading: true }).then((res) => {
        this.clientOptions = res;
        if (this.$route.params.client) {
          vm.getClient({ client: this.$route.params.client }).then((response) => {
            vm.client = response;
          });
        }
      });
    },
    setCustomLabel(client) {
      const name = this.$h.formatName(client);
      const email = "<" + client.email + ">";
      const res = name + " " + email;
      return `${res}`;
    },
    loadClients(query) {
      if (query.length > 1) {
        this.isLoading = true;
        const params = {
          per_page: "1000",
          search: query,
        };
        this.getClientList({ params, customLoading: true }).then((res) => {
          this.clientOptions = res;
          this.isLoading = false;
        });
      } else if (query.length === 0) {
        this.isLoading = true;
        const params = {
          per_page: "1000",
          order_col: "last_name",
          order_dir: "ASC",
          search: "",
        };
        this.getClientList({ params, customLoading: true }).then((res) => {
          this.clientOptions = res;
          this.isLoading = false;
        });
      }
    },
    getTemplates() {
      const vm = this;
      vm.getTeamTemplates({
        customLoading: true,
        params: { per_page: "100" },
      }).then(() => {
        vm.templateOptions = vm.templates;
        if (vm.teamsSettings[vm.$route.params.team].lastSelectedDesignTemplate) {
          vm.projectTemplate = vm.teamsSettings[vm.$route.params.team].lastSelectedDesignTemplate;
        }
      });
    },
    submitProject() {
      const vm = this;
      if (vm.createNewClient) {
        const data = {
          last_name: vm.lastName,
          first_name: vm.firstName,
          email: vm.email,
          gender: vm.gender && vm.gender.value,
          locale: vm.locale && vm.locale.value,
        };
        vm.$validator.validateAll("createProject").then((result) => {
          if (result) {
            vm.setLastSelectedDesignTemplate(vm.projectTemplate);
            vm.isCustomLoading = true;
            vm.createClient({ customLoading: true, data })
              .then((res) => {
                vm.resetCreateClientForm();
                const data = {
                  title: vm.projectName,
                  owner_uid: res.uid,
                  template_uid: vm.projectTemplate.uid,
                  private: vm.privateProject,
                };
                vm.createProject({
                  customLoading: true,
                  data,
                })
                  .then(() => {
                    vm.$goTo("QuickLaunchWithClientAndProject", {
                      client: vm.currentProject.owner.uid,
                      project: vm.currentProject.uid,
                    });
                    vm.currentStep += 1;
                    vm.steps = [
                      { number: 1, active: false, success: true },
                      { number: 2, active: true, success: false },
                      { number: 3, active: true, success: false },
                      {
                        number: 4,
                        active: false,
                        success: false,
                        icon: require("@icon/Icon=rocket.svg"),
                        iconClass: "icon-rocket",
                        iconColor: "#5057AF",
                      },
                    ];
                    vm.$snotify.success(vm.$t("quickLaunch.toasts.createProject.success"), vm.$t("toasts.success"), {
                      icon: vm.$successIcon,
                    });
                  })
                  .catch(() => {
                    this.isCustomLoading = false;
                    vm.$snotify.error(vm.$t("quickLaunch.toasts.createProject.error"), vm.$t("toasts.error"), {
                      timeout: 0,
                      icon: vm.$errorIcon,
                    });
                  });
              })
              .catch((err) => {
                vm.isCustomLoading = false;
                if (
                  err.response &&
                  !err.response.data.error.custom &&
                  err.response.data.error.http_code === 422 &&
                  err.response.data.error.validation.email
                ) {
                  vm.$validator.errors.add({
                    field: "createProject.email",
                    msg: vm.$t("errors.emailExists"),
                  });
                  vm.$snotify.error(vm.$t("quickLaunch.toasts.createProject.validation"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                } else {
                  vm.$snotify.error(vm.$t("quickLaunch.toasts.createProject.error"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                }
              });
          } else {
            vm.$snotify.error(vm.$t("quickLaunch.toasts.createProject.validation"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
      } else {
        vm.$validator.validateAll("createProject").then((result) => {
          if (result) {
            vm.setLastSelectedDesignTemplate(vm.projectTemplate);
            vm.isCustomLoading = true;
            const data = {
              title: vm.projectName,
              owner_uid: vm.client.uid,
              template_uid: vm.projectTemplate.uid,
              private: vm.privateProject,
            };
            vm.createProject({
              customLoading: true,
              data,
            })
              .then(() => {
                vm.currentStep += 1;
                vm.$goTo("QuickLaunchWithClientAndProject", {
                  client: vm.currentProject.owner.uid,
                  project: vm.currentProject.uid,
                });
                vm.$snotify.success(vm.$t("quickLaunch.toasts.createProject.success"), vm.$t("toasts.success"), {
                  icon: vm.$successIcon,
                });
              })
              .catch(() => {
                vm.isCustomLoading = false;
                vm.$snotify.error(vm.$t("quickLaunch.toasts.createProject.error"), vm.$t("toasts.error"), {
                  timeout: 0,
                  icon: vm.$errorIcon,
                });
              });
          } else {
            vm.$snotify.error(vm.$t("quickLaunch.toasts.createProject.validation"), vm.$t("toasts.error"), {
              timeout: 0,
              icon: vm.$errorIcon,
            });
          }
        });
      }
    },
    initProjectData() {
      const vm = this;
      const params = {
        per_page: "1000",
        order_col: "last_name",
        order_dir: "ASC",
        search: "",
      };
      const p1 = vm.getTeamContacts({ params, customLoading: true }).then((res) => {
        vm.contactOptions = res.data.data;
      });
      const p2 = vm
        .getTemplateSnippets({
          template: vm.currentProject.template.uid,
          customLoading: true,
          params: {
            per_page: "1000",
            is_text: true,
          },
        })
        .then((res) => {
          vm.textTemplateSnippetOptions = res.data.data;
          vm.textDefaultTemplateSnippet = vm.textTemplateSnippetOptions.find((t) => {
            return vm.$h.templateSnippetsStatus(t.status, "default", false);
          });
        })
        .catch(() => {});
      const p3 = vm
        .getTemplateSnippets({
          template: vm.currentProject.template.uid,
          customLoading: true,
          params: {
            per_page: "1000",
            is_email: true,
          },
        })
        .then((res) => {
          vm.emailTemplateSnippetOptions = res.data.data;
          vm.emailDefaultTemplateSnippet = vm.emailTemplateSnippetOptions.find((t) => {
            return vm.$h.templateSnippetsStatus(t.status, "default", false);
          });
          vm.emailTemplateSnippetsReady = true;
        })
        .catch(() => {});
      const p4 = vm
        .getProjectComponents({
          customLoading: true,
          project: vm.currentProject.uid,
          params: { quicklaunch: 1 },
        })
        .then(() => {})
        .catch((err) => {
          console.log(err);
        });
      const p5 = vm.getProjectPages({
        customLoading: true,
        project: vm.currentProject.uid,
      });
      Promise.all([p1, p2, p3, p4, p5])
        .then(() => {
          vm.currentProjectPages.forEach((page) => {
            vm.switchPages.push({ uid: page.uid, visible: page.visible });
            if (page.visible || page.overridable) {
              vm.pagesOptions.push({
                name: page.name,
                uid: page.uid,
                overridable: page.overridable,
                visible: page.visible,
              });
            }
            if (page.visible) {
              vm.selectedPages.push({
                name: page.name,
                uid: page.uid,
                overridable: page.overridable,
                visible: page.visible,
              });
            }
          });
          this.isCustomLoading = false;
        })
        .catch(() => {
          this.isCustomLoading = false;
        });
    },
    openBuilder(preview) {
      if (this.currentStep === 1 || this.currentStep === 2) {
        const vm = this;
        vm.getProjectBuilderSession({
          params: {
            project: vm.currentProject.uid,
          },
          data: {
            return_url: window.location.href,
            preview: preview,
          },
        })
          .then(() => {
            location.href = vm.builderSession;
          })
          .catch(() => {});
      }
    },
    setSentStatus(data, redirect = true) {
      const vm = this;
      if (
        (data && data.sendAlways) ||
        !(
          (this.currentProject.status & this.currentProjectStati["POSTED"].bit) ===
          this.currentProjectStati["POSTED"].bit
        )
      ) {
        vm.setProjectPosted({
          params: { project: vm.currentProject.uid },
          data,
        }).then(() => {
          if (redirect) this.completeQuickLaunch();
        });
      } else {
        if (redirect) this.completeQuickLaunch();
      }
    },
    goToShareProject() {
      if (this.currentStep === 1) {
        this.$nextTick(() => {
          this.$scrollTo("#send");
        });
      }
    },
    completeQuickLaunch() {
      this.$eventbus.$emit("saveComponents");
      this.continueCompleteQuickLaunch();
    },
    setUnsavedComponents(uid) {
      if (!this.unsavedComponents.includes(uid)) {
        this.unsavedComponents.push(uid);
      }
    },
    componentSavedSuccess(uid, proceed = false) {
      let i;
      i = this.unsavedComponents.indexOf(uid);
      if (i > -1) {
        this.unsavedComponents.splice(i, 1);
      }
      i = this.unsavedComponentsWithError.indexOf(uid);
      if (i > -1) {
        this.unsavedComponentsWithError.splice(i, 1);
      }
      if (proceed) this.continueCompleteQuickLaunch();
    },
    componentSavedError(uid) {
      let i = this.unsavedComponents.indexOf(uid);
      if (i > -1) {
        this.unsavedComponents.splice(i, 1);
      }
      if (!this.unsavedComponentsWithError.includes(uid)) {
        this.unsavedComponentsWithError.push(uid);
      }
    },
    continueCompleteQuickLaunch() {
      if (this.unsavedComponents.length < 1 && this.unsavedComponentsWithError.length < 1) {
        if (this.currentStep === 1) {
          this.$goTo(
            "QuickLaunchWithClientAndProject",
            {
              client: this.currentProject.owner.uid,
              project: this.currentProject.uid,
            },
            { completed: 1 }
          );
          this.steps = [
            { number: 1, active: false, success: true },
            { number: 2, active: false, success: true },
            { number: 3, active: false, success: true },
            {
              number: 4,
              active: true,
              success: false,
              icon: require("@icon/Icon=rocket.svg"),
              iconClass: "icon-rocket",
              iconColor: "#ffffff",
            },
          ];
        }
      } else if (this.unsavedComponents.length < 1 && this.unsavedComponentsWithError.length > 0) {
        this.$snotify.error(this.$t("quickLaunch.toasts.completeQuickLaunch.error"), this.$t("toasts.error"), {
          timeout: 0,
          icon: this.$errorIcon,
        });
        this.$nextTick(() => {
          this.unsavedComponentsWithError.forEach((key) => {
            this.$eventbus.$emit("openBox", key);
          });
          const el = ".accordion-header-" + this.unsavedComponentsWithError[0];
          this.$scrollTo(el, 500, {});
        });
      }
    },
    copyLinkCallback(setSent = true) {
      if (setSent) {
        this.setSentStatus(false, false);
      }
      this.$snotify.success(this.$t("toasts.copiedLink.success"), this.$t("toasts.success"), {
        icon: this.$successIcon,
      });
    },
    addUnsavedComponents(uid) {
      this.unsavedComponents.push(uid);
    },
    removeUnsavedComponents(uid) {
      this.unsavedComponents.push(uid);
    },
  },
  computed: {
    ...mapGetters({
      teams: "auth_get_teams",
      teamLanguages: "teams_get_languages",
      templates: "templates_get_templates",
      currentProject: "projects_get_current_project",
      currentProjectStati: "projects_get_current_project_stati",
      currentProjectComponents: "quicklaunch_get_components",
      currentProjectPages: "quicklaunch_get_pages",
      showTips: "global_get_show_tips",
      showAlways: "global_get_show_tips_icon",
      builderSession: "projects_get_builder_session",
      startingRoute: "quicklaunch_get_redirect_route",
      teamsSettings: "global_get_teams_settings",
    }),
    currentAccordionBoxes() {
      let accordionBoxes = [];
      this.currentProjectComponents.forEach((comp) => {
        let title = "";
        if (comp.config.quicklaunch_title) {
          title = comp.config.quicklaunch_title;
        } else if (comp.name && comp.page_title && !comp.title) {
          title = comp.name + " / " + comp.page_title;
        } else if (!comp.name && !comp.page_title && comp.title) {
          title = comp.title;
        }
        let icon = "Icon=folder.svg";
        switch (comp.tagname) {
          case "tb-text-template":
            icon = "Icon=templatevorlage.svg";
            break;
          case "tb-file-list":
            icon = "Icon=dateiliste.svg";
            break;
          case "tb-contact":
            icon = "Icon=kontaktkarte.svg";
            break;
          case "tb-title":
            icon = "Icon=title.svg";
            break;
          case "tb-html":
            icon = "Icon=format.svg";
            break;
        }
        accordionBoxes.push({
          uid: comp.uid,
          title,
          icon,
          open: comp.config.quicklaunch_opened,
          tagName: comp.tagname,
        });
      });
      return accordionBoxes;
    },
  },
};
</script>
